/**
 * Retrieve fan-out for the site entity.
 *
 * The composite GET /sites/{id}/ response arrives as one payload; this thunk
 * distributes it into the site entity's normalized stores — site identity,
 * unit, active alarms, the meta sidecar (feature associations + permissions),
 * and the media-summary rail — so every consumer reads its own store via a
 * selectX(siteId) selector. Called from the retrieveSite query's
 * onQueryStarted once the fetch fulfils.
 *
 * Contains:
 * - ingestSiteSnapshot(siteId, payload) — the fan-out thunk
 */
import { setMediaSummary } from '@sophocles/features/media/slice';

import { COMMAND_ACTIVITY_SOURCE, replaceCommandActivity } from './siteCommands';
import { replaceAlarms } from './sliceAlarms';
import { setMeta } from './sliceMeta';
import { replaceSite } from './sliceSite';
import { removeUnit, replaceUnit } from './sliceUnit';

/**
 * Fan a composite retrieve out into the site entity's normalized stores.
 *
 * @param {number|string} siteId
 * @param {object} payload - the flattened retrieveSite cache shape
 *   ({ site, unit, alarms, command_activity, media_summary,
 *   feature_associations, _permissions, alarmsGeneratedAt })
 * @param {object} options
 * @param {number|null} options.requestVersion - retrieve request-start order
 */
export const ingestSiteSnapshot =
  (siteId, payload, { requestVersion = null } = {}) =>
  dispatch => {
    dispatch(replaceSite({ siteId, site: payload?.site ?? null }));

    if (payload?.unit == null) {
      dispatch(removeUnit({ siteId, source: COMMAND_ACTIVITY_SOURCE.REST, requestVersion }));
    } else {
      dispatch(
        replaceUnit({
          siteId,
          unit: payload.unit,
          source: COMMAND_ACTIVITY_SOURCE.REST,
          requestVersion,
        }),
      );
    }

    dispatch(replaceAlarms({ siteId, list: payload?.alarms ?? [], generatedAt: payload?.alarmsGeneratedAt ?? null }));
    dispatch(
      replaceCommandActivity({
        siteId,
        activity: payload?.command_activity ?? null,
        source: COMMAND_ACTIVITY_SOURCE.REST,
        requestVersion,
      }),
    );
    dispatch(
      setMeta({
        siteId,
        featureAssociations: payload?.feature_associations ?? [],
        permissions: payload?._permissions ?? null,
      }),
    );

    if (payload?.media_summary != null) {
      const { photo_count, image_count, recent } = payload.media_summary;
      dispatch(
        setMediaSummary({
          siteId,
          photoCount: photo_count ?? 0,
          imageCount: image_count ?? 0,
          recent: recent ?? [],
        }),
      );
    }
  };
